/**
 * ч.1357: ключ КЕП для Держстату — підпис і відправка звіту у приймальний шлюз,
 * отримання квитанцій. Ланцюг — як для ДПС (ключ не покидає браузер): CAdES-підпис →
 * UA1_SIGN → шифрування на сертифікат шлюзу Держстату → UA1_CRYPT → TRANSPORTABLE з
 * PRG_TYPE → наш backend → SOAP Send. Квитанції шлюз віддає зашифрованими на ключ
 * респондента: їх розшифровує браузер і повертає XML на сервер (статус подання).
 */
import { useEffect, useRef, useState } from 'react';
import { orgs } from '@/api/catalogs';
import {
  statSubmissions, type StatSubmission, SUB_STATUS_COLOR, SUB_STATUS_LABEL,
} from '@/api/statReports';
import {
  buildStatContainer, bytesToBase64, developStatReceiptToXml, getCAs, isKeyReaded, loadEuSign,
  readPrivateKey, resetKey, sessionKeyOwner, signStatArgument,
  type EuSignCA, type KeyOwnerInfo,
} from '@/shared/eusign/eusign';

export type KeyStage = 'idle' | 'loading' | 'ready' | 'reading' | 'keyed';

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Зчитування ключа (файл + пароль, АЦСК) — спільний блок редактора і кабінету. */
export function useStatKey() {
  const [stage, setStage] = useState<KeyStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [cas, setCas] = useState<EuSignCA[]>([]);
  const [caIdx, setCaIdx] = useState('auto');
  const [password, setPassword] = useState('');
  const [owner, setOwner] = useState<KeyOwnerInfo | null>(null);
  const keyFileRef = useRef<{ name: string; bytes: Uint8Array } | null>(null);

  async function activate() {
    setStage('loading'); setError(null);
    try {
      await loadEuSign();
      setCas(await getCAs());
      const sess = sessionKeyOwner();
      if (sess && isKeyReaded()) { setOwner(sess); setStage('keyed'); } else setStage('ready');
    } catch (e: any) { setError(e?.message ?? String(e)); setStage('idle'); }
  }
  function onKeyFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onloadend = () => { keyFileRef.current = { name: f.name, bytes: new Uint8Array(rd.result as ArrayBuffer) }; setOwner(null); };
    rd.readAsArrayBuffer(f);
  }
  async function readKey() {
    if (!keyFileRef.current) { setError('Оберіть файл ключа'); return; }
    if (!password) { setError('Вкажіть пароль ключа'); return; }
    setStage('reading'); setError(null);
    try {
      const ca = caIdx === 'auto' ? null : cas[Number(caIdx)] ?? null;
      setOwner(await readPrivateKey(keyFileRef.current.bytes, password, ca));
      setPassword(''); setStage('keyed');
    } catch (e: any) { setError(e?.message ?? String(e)); setStage('ready'); }
  }
  function another() { resetKey(); setOwner(null); setStage('ready'); }
  return { stage, setStage, error, setError, cas, caIdx, setCaIdx, password, setPassword, owner, activate, onKeyFile, readKey, another };
}

export function StatKeyBox({ k, title = 'Ключ КЕП' }: { k: ReturnType<typeof useStatKey>; title?: string }) {
  if (k.stage === 'idle' || k.stage === 'loading') {
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={k.activate} disabled={k.stage === 'loading'}>
          {k.stage === 'loading' ? 'завантаження криптобібліотеки…' : 'зчитати ключ КЕП'}
        </button>
        <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
          підпис, шифрування і розшифрування квитанцій — у вашому браузері, ключ нікуди не передається
        </span>
        {k.error && <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem', flexBasis: '100%' }}>{k.error}</div>}
      </div>
    );
  }
  return (
    <div>
      {!k.owner && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ minWidth: 260 }}>
            <label>Надавач послуг (АЦСК) — необовʼязково</label>
            <select value={k.caIdx} onChange={(e) => k.setCaIdx(e.target.value)}>
              <option value="auto">визначити автоматично</option>
              {k.cas.map((c, i) => <option key={i} value={i}>{c.issuerCNs[0]}</option>)}
            </select>
          </div>
          <div className="field" style={{ minWidth: 240 }}>
            <label>Файл ключа (Key-6.dat / *.pfx / *.zs2)</label>
            <input type="file" onChange={k.onKeyFile} accept=".dat,.pfx,.pk8,.zs2,.jks,.p12" />
          </div>
          <div className="field" style={{ width: 170 }}>
            <label>Пароль ключа</label>
            <input type="password" value={k.password} onChange={(e) => k.setPassword(e.target.value)} />
          </div>
          <button onClick={k.readKey} disabled={k.stage === 'reading'}>{k.stage === 'reading' ? 'зчитування…' : 'зчитати ключ'}</button>
        </div>
      )}
      {k.owner && (
        <div style={{ background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)', borderRadius: 6,
          padding: '0.4rem 0.6rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>✓ {title}: <b>{k.owner.subjCN}</b>
            {k.owner.subjDRFOCode ? ` (РНОКПП ${k.owner.subjDRFOCode})` : ''}
            {k.owner.subjEDRPOUCode ? `, ЄДРПОУ ${k.owner.subjEDRPOUCode}` : ''} — {k.owner.issuerCN}</span>
          <button style={{ marginLeft: 'auto', padding: '0.15rem 0.5rem', fontSize: '0.75rem' }} onClick={k.another}>інший ключ</button>
        </div>
      )}
      {k.error && <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem', marginTop: 4 }}>{k.error}</div>}
    </div>
  );
}

export interface ReceiptsOutcome {
  received: number;
  linked: { file_name: string | null; kind: string | null; accepted: boolean | null; text: string; status: string | null }[];
  errors: string[];
}

/**
 * Забрати квитанції зі шлюзу для організації: підписаний e-mail → ReceiveAll → кожне
 * повідомлення розшифрувати ключем → XML на сервер (звʼязування зі звітом, статус) →
 * оброблені вилучити зі шлюзу (підписаний перелік кодів).
 */
/** ч.1423: шлюз віддає квитанції за поштою, з якою подано звіт; вона може відрізнятись від картки. */
export function receiptEmails(cardEmail: string, submissions: { sender_email: string | null }[]): string[] {
  const byKey = new Map<string, string>();
  for (const raw of [cardEmail, ...submissions.map((s) => s.sender_email ?? '')]) {
    const email = raw.trim();
    if (email && !byKey.has(email.toLowerCase())) byKey.set(email.toLowerCase(), email);
  }
  return [...byKey.values()];
}

export async function fetchStatReceipts(organizationId: string, email: string): Promise<ReceiptsOutcome> {
  const out: ReceiptsOutcome = { received: 0, linked: [], errors: [] };
  const page = await statSubmissions.receive({ organization_id: organizationId, signed_email_base64: signStatArgument(email) });
  out.received = page.items.length;
  const done: string[] = [];
  for (const m of page.items) {
    try {
      const xml = developStatReceiptToXml(m.body_base64);
      const r = await statSubmissions.decrypted(m.id, xml);
      out.linked.push({ file_name: r.file_name, kind: r.kind, accepted: r.accepted, text: r.text, status: r.submission?.status ?? null });
      done.push(m.gate_msg_id);
    } catch (e: any) {
      out.errors.push(`${m.file_name ?? m.gate_msg_id}: ${e?.response?.data?.detail ?? e?.message ?? String(e)}`);
    }
  }
  if (done.length) {
    try {
      await statSubmissions.deleteMessages({ organization_id: organizationId, ids: done, signed_ids_base64: signStatArgument(done.join('\n')) });
    } catch (e: any) {
      out.errors.push(`вилучення зі шлюзу: ${e?.response?.data?.detail ?? e?.message ?? String(e)}`);
    }
  }
  return out;
}

export function StatusLine({ sub }: { sub: StatSubmission }) {
  return (
    <div style={{ background: 'var(--bg-soft)', color: SUB_STATUS_COLOR[sub.status] ?? 'var(--text)', borderRadius: 6,
      padding: '0.4rem 0.6rem', margin: '0.5rem 0', fontSize: '0.82rem', fontWeight: sub.status === 'ACCEPTED' ? 600 : 400 }}>
      {SUB_STATUS_LABEL[sub.status] ?? sub.status}
      {sub.status === 'ERROR' && sub.error_message ? ` — ${sub.error_message}` : ''}
      {sub.sent_at ? ` · відправлено ${new Date(sub.sent_at).toLocaleString('uk-UA')}` : ''}
    </div>
  );
}

interface PanelProps {
  organizationId: string;
  reportId: string;
  /** Файл звіту (base64) і e-mail відправника — з /files (після перевірки проти XSD). */
  getFiles: () => Promise<{ fname: string; xml_base64: string; sender_email: string }>;
  sub: StatSubmission | null;
  onSubmitted?: (s: StatSubmission) => void;
  onReceipts?: (o: ReceiptsOutcome) => void;
}

/** Панель на звіті: підписати і відправити; після відправки — отримати квитанції. */
export default function StatSignPanel({ organizationId, reportId, getFiles, sub, onSubmitted, onReceipts }: PanelProps) {
  const k = useStatKey();
  const [busy, setBusy] = useState<'send' | 'receipts' | null>(null);
  const [email, setEmail] = useState('');
  useEffect(() => {
    let alive = true;
    orgs.list({ limit: 200 }).then((r) => { if (alive) setEmail(r.items.find((o) => o.id === organizationId)?.email ?? ''); }).catch(() => {});
    return () => { alive = false; };
  }, [organizationId]);
  const sent = !!sub && ['SENT', 'DELIVERED', 'ACCEPTED'].includes(sub.status);

  async function signAndSend() {
    if (!k.owner) return;
    setBusy('send'); k.setError(null);
    try {
      const f = await getFiles();
      const senderEmail = (f.sender_email || email).trim();
      if (!senderEmail) throw new Error('У картці організації не вказано e-mail — на нього шлюз надсилає квитанції');
      const container = await buildStatContainer(b64ToBytes(f.xml_base64), f.fname, senderEmail, k.owner.issuerCN);
      const res = await statSubmissions.submit({
        organization_id: organizationId, report_id: reportId, file_name: f.fname,
        container_base64: bytesToBase64(container),
        signer_edrpou: k.owner.subjEDRPOUCode ?? k.owner.subjDRFOCode, sender_email: senderEmail,
      });
      onSubmitted?.(res.submission);
      if (res.gate_result && res.gate_result !== 'GATE_OK') k.setError(`Шлюз Держстату: ${res.message ?? res.gate_result}`);
    } catch (e: any) {
      k.setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally { setBusy(null); }
  }
  async function receipts() {
    if (!k.owner) return;
    setBusy('receipts'); k.setError(null);
    try {
      const em = (sub?.sender_email || email).trim();
      if (!em) throw new Error('Не відомий e-mail відправника');
      const o = await fetchStatReceipts(organizationId, em);
      onReceipts?.(o);
      if (o.errors.length) k.setError(o.errors.join('\n'));
    } catch (e: any) {
      k.setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally { setBusy(null); }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', marginTop: '0.75rem' }}>
      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Подання до Держстату</div>
      {sub && <StatusLine sub={sub} />}
      <StatKeyBox k={k} />
      {k.owner && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <button className="primary" onClick={signAndSend} disabled={busy !== null || sent}
            title={sent ? 'Звіт уже відправлено; для виправлення створіть виправний звіт' : undefined}>
            {busy === 'send' ? 'відправка…' : 'підписати і відправити до Держстату'}
          </button>
          <button onClick={receipts} disabled={busy !== null || !sub || sub.status === 'DRAFT'}
            title={!sub || sub.status === 'DRAFT' ? 'Спершу відправте звіт' : 'Запитати квитанції у шлюзу (ключем)'}>
            {busy === 'receipts' ? 'запит квитанцій…' : 'отримати квитанції'}
          </button>
          <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
            підпис → шифрування на сертифікат шлюзу → транспортний контейнер → шлюз Держстату
          </span>
        </div>
      )}
      {sub?.receipt_text && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', whiteSpace: 'pre-wrap', background: 'var(--bg-soft)',
          borderRadius: 6, padding: '0.5rem 0.6rem' }}>
          {sub.receipt_text}
        </div>
      )}
    </div>
  );
}
